import {
  Calendar,
  DollarSign,
  Image as ImgIcon,
  MapPin,
  Play,
  Star,
  Tag,
  PhoneCall,
  AlertTriangle,
  MessageSquare,
} from 'lucide-react';

const status = {
  pending: { label: 'Pending', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  assigned: { label: 'Assigned', color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)' },
  in_progress: { label: 'In Progress', color: '#0491e1', bg: 'rgba(4,145,225,0.10)' },
  awaiting_confirmation: { label: 'Awaiting Confirmation', color: '#06b6d4', bg: 'rgba(6,182,212,0.10)' },
  completed: { label: 'Completed', color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
  raise_dispute: { label: 'Dispute Raised', color: '#f97316', bg: 'rgba(249,115,22,0.10)' },
  dispute_frozen: { label: 'Dispute Frozen', color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  cancelled: { label: 'Cancelled', color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  refunded: { label: 'Refunded', color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
  admin_review: { label: 'Admin Review', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  bid_sent: { label: 'Bids Received', color: '#0491e1', bg: 'rgba(4,145,225,0.10)' },
};

const JobCard = ({
  b,
  baseUrlForFile,
  reviews,
  jobReviews,
  setLightbox,
  setBidsModalFor,
  handleCancel,
  setDisputeFor,
  setReviewFor,
  openDisputeChat,
}) => {
  const workerProofs = b.whworkerjobproofs || [];
  const st = status[b.status] || status.pending;
  const rv = reviews[b.id] || {};
  const workerBids = b.whplacebids || [];
  const statusKey = (b.status || '').toLowerCase();

  const phone =
    b.whserviceprovider?.phoneNumber ||
    b.whserviceprovider?.phone_number;

  const showCall = statusKey === 'assigned' && !!phone;

  return (
    <div className="mbid-card" key={b.id}>

      {/* ── Card Top ── */}
      <div className="mbid-card__top">
        <div className="mbid-card__top-left">
          <h3 className="mbid-card__title">{b.jobTitle}</h3>
          <div className="mbid-card__meta">
            <span><Tag size={13} />{b.serviceType}</span>
            <span><DollarSign size={13} /> {b.budget}</span>
            <span><MapPin size={13} />{b.city}</span>
            <span><Calendar size={13} />{b.deadline}</span>
          </div>
        </div>

        <div className="mbid-card__top-right">
          {showCall && (
            <a
              href={`tel:${phone}`}
              className="mbid-call-icon"
              title="Call Worker"
            >
              <PhoneCall size={18} />
            </a>
          )}
          <div
            className="mbid-badge"
            style={{ color: st.color, background: st.bg }}
          >
            {st.label}
          </div>
        </div>
      </div>

      {/* ── Description ── */}
      <p className="mbid-card__note">{b.description}</p>

      {/* ── Address ── */}
      <div className="mbid-card__meta">
        <span><MapPin size={13} />{b.address}</span>
      </div>

      {/* ── Customer Media ── */}
      {b.status !== 'awaiting_confirmation' &&
        (b.whcustomerjobmedias ?? []).length > 0 && (
          <div className="mbid-media-row">
            <div className="mbid-media-label"><ImgIcon size={13} />Customer Media</div>
            <div className="mbid-media-thumbs">
              {(b.whcustomerjobmedias ?? []).map((m, i) => {
                const fileUrl = `${baseUrlForFile}/${m.file}`;
                const isVideo = m.file?.toLowerCase().endsWith('.mp4');
                return (
                  <button key={i} className="mbid-media-thumb"
                    onClick={() => setLightbox({ type: isVideo ? 'video' : 'image', url: fileUrl })}>
                    {isVideo ? (
                      <><video src={fileUrl} className="mbid-media-thumb__img" />
                        <div className="mbid-media-thumb__play"><Play size={18} color="#fff" /></div></>
                    ) : (
                      <img src={fileUrl} alt="" className="mbid-media-thumb__img" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      {/* ══ BID_SENT → Bids modal button ══ */}
      {b.status === 'bid_sent' && (
        <div className="mbid-btn-row">
          <button
            className="mbid-btn mbid-btn--bids"
            onClick={() => setBidsModalFor(b)}
          >
            View All Bids ({workerBids.length})
          </button>
        </div>
      )}

      {/* ══ PENDING → Cancel ══ */}
      {b.status === 'pending' && (
        <div className="mbid-btn-row">
          <button className="mbid-btn mbid-btn--cancel" onClick={() => handleCancel(b.id)}>
            Cancel Job
          </button>
        </div>
      )}

      {/* ══ IN PROGRESS → Raise Dispute ══ */}
      {b.status === 'in_progress' && (
        <div className="mbid-btn-row">
          <button className="mbid-btn mbid-btn--dispute" onClick={() => setDisputeFor(b)}>
            <AlertTriangle size={14} /> Raise Dispute
          </button>
        </div>
      )}

      {/* ══ AWAITING CONFIRMATION ══ */}
      {b.status === 'awaiting_confirmation' && (
        <>
          {workerProofs.length > 0 && (
            <div className="mbid-media-row">
              <div className="mbid-media-label"><ImgIcon size={13} />Worker Proof</div>
              <div className="mbid-media-thumbs">
                {workerProofs.map((m, i) => {
                  const fileUrl = `${baseUrlForFile}/${m.file}`;
                  const isVideo = m.extension?.toLowerCase() === 'mp4';
                  return (
                    <button key={i} className="mbid-media-thumb"
                      onClick={() => setLightbox({ type: isVideo ? 'video' : 'image', url: fileUrl })}>
                      {isVideo
                        ? <video src={fileUrl} className="mbid-media-thumb__img" />
                        : <img src={fileUrl} className="mbid-media-thumb__img" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mbid-btn-row">
            <button
              className="mbid-btn mbid-btn--confirm"
              onClick={() => setReviewFor(b)}
            >
              <Star size={14} fill="currentColor" /> Rate & Complete
            </button>
            <button className="mbid-btn mbid-btn--dispute" onClick={() => setDisputeFor(b)}>
              <AlertTriangle size={14} /> Raise Dispute
            </button>
          </div>
        </>
      )}

      {/* ══ RAISE_DISPUTE → Open Chat ══ */}
      {b.status === 'raise_dispute' && (
        <div className="mbid-btn-row">
          <button className="mbid-btn mbid-btn--chat" onClick={() => openDisputeChat(b)}>
            <MessageSquare size={14} /> Open Dispute Chat
          </button>
        </div>
      )}

      {/* ══ COMPLETED → Show Review & Worker Reply ══ */}
      {b.status === 'completed' &&
        (
          (jobReviews[b.id]?.customer?.rating > 0 || jobReviews[b.id]?.customer?.review) ||
          (jobReviews[b.id]?.worker?.rating > 0 || jobReviews[b.id]?.worker?.review)
        ) && (
          <div className="mbid-review-display">

            {/* Customer Review */}
            {jobReviews[b.id]?.customer &&
              (jobReviews[b.id]?.customer?.rating > 0 || jobReviews[b.id]?.customer?.review) && (
                <div className="aj-review-display">
                  <h4>Customer Review</h4>
                  <div className="aj-stars">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star
                        key={s}
                        size={13}
                        fill={s <= Number(jobReviews[b.id].customer.rating) ? '#f59e0b' : '#e5e7eb'}
                        color={s <= Number(jobReviews[b.id].customer.rating) ? '#f59e0b' : '#e5e7eb'}
                      />
                    ))}
                  </div>
                  <p>{jobReviews[b.id].customer.review}</p>
                </div>
              )}

            {/* Worker Review */}
            {jobReviews[b.id]?.worker &&
              (jobReviews[b.id]?.worker?.rating > 0 || jobReviews[b.id]?.worker?.review) && (
                <div className="aj-review-display">
                  <h4>Worker Review</h4>
                  <div className="aj-stars">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star
                        key={s}
                        size={13}
                        fill={s <= Number(jobReviews[b.id].worker.rating) ? '#f59e0b' : '#e5e7eb'}
                        color={s <= Number(jobReviews[b.id].worker.rating) ? '#f59e0b' : '#e5e7eb'}
                      />
                    ))}
                  </div>
                  <p>{jobReviews[b.id].worker.review}</p>
                </div>
              )}

          </div>
        )}

    </div>
  );
};

export default JobCard;